import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, Button, Descriptions, Row, Col, Space, Spin, Tag, message } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getGuidelineById } from '@/api/guidelines';
import { Guideline } from '@/types/community/guideline';
import { useAuth } from '@/contexts/AuthContext';
import GuidelineModal from '@/components/features/community/GuidelineModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteGuideline } from '@/api/guidelines';



const GuidelineDetailsPage: React.FC = () => {
  const { t } = useTranslation(['guidelines', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { guideline: Guideline } | null;
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();

  // State for modals
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<Error | undefined>(undefined);

  // Queries - CRITICAL: Only fetch if no state data is available
  const { data: guideline, isLoading } = useQuery({
    queryKey: ['guidelines', id],
    queryFn: () => getGuidelineById(id!, firestoreUser!),
    enabled: !!id && !state?.guideline && !!firestoreUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Use state data if available, otherwise use fetched data
  const displayGuideline = state?.guideline || guideline;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (guidelineId: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteGuideline(guidelineId, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('guidelines:guidelineDeleted'));
      queryClient.invalidateQueries({ queryKey: ['guidelines'] });
      setIsDeleteModalVisible(false);
      setDeleteError(undefined);
      navigate('/community/guidelines');
    },
    onError: (error: Error) => {
      setDeleteError(error);
    }
  });

  // Handlers
  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalVisible(true);
    setDeleteError(undefined);
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['guidelines', id] });
  };

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setDeleteError(undefined);
  };

  const handleErrorDismiss = () => {
    setDeleteError(undefined);
  };

  // Loading state - only show if no state data is available
  if (isLoading && !state?.guideline) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayGuideline) {
    return <Card>Guideline not found</Card>;
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              {t('common:back')}
            </Button>
            {/* <Title level={3}>{displayGuideline.title}</Title> */}
            {/* <Text strong>{displayGuideline.title}</Text> */}
            {/* <Paragraph>{displayGuideline.content}</Paragraph> */}
            {/* <Tag color="blue">{displayGuideline.category}</Tag> */}
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>
      
      {/* Content */}
      <Card>
        <Descriptions bordered column={1}>
          <Descriptions.Item label={t('guidelines:table.title')}>
            -
          </Descriptions.Item>
          
          <Descriptions.Item label={t('guidelines:table.content')}>
            -
          </Descriptions.Item>
          
          <Descriptions.Item label={t('guidelines:table.category')}>
            -
          </Descriptions.Item>
          
          {displayGuideline.effectiveDate && (
            <Descriptions.Item label={t('guidelines:table.effectiveDate')}>
              {new Date(displayGuideline.effectiveDate).toLocaleDateString()}
            </Descriptions.Item>
          )}
          
          {displayGuideline.expiryDate && (
            <Descriptions.Item label={t('guidelines:table.expiryDate')}>
              {new Date(displayGuideline.expiryDate).toLocaleDateString()}
            </Descriptions.Item>
          )}
          
          {displayGuideline.version && (
            <Descriptions.Item label={t('guidelines:table.version')}>
              {displayGuideline.version}
            </Descriptions.Item>
          )}
          
          {displayGuideline.author && (
            <Descriptions.Item label={t('guidelines:table.author')}>
              {displayGuideline.author.name}
            </Descriptions.Item>
          )}
          
          {displayGuideline.status && (
            <Descriptions.Item label={t('guidelines:table.status')}>
              <Tag color={displayGuideline.status === 'active' ? 'green' : displayGuideline.status === 'draft' ? 'orange' : 'default'}>
                {t(`guidelines:statuses.${displayGuideline.status}`)}
              </Tag>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Edit Modal */}
      <GuidelineModal
        open={isEditModalOpen}
        guideline={displayGuideline}
        onSuccess={handleEditSuccess}
        onCancel={handleEditCancel}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('guidelines:confirmDeleteTitle')}
        error={deleteError}
        onErrorDismiss={handleErrorDismiss}
      />
    </div>
  );
};

export default GuidelineDetailsPage; 